/**
 * Liste de cartes de pièces (à valider, à arbitrer, à télécharger) : chaque
 * pièce a sa carte, rechargée sans toucher aux cartes déjà là, pour ne pas
 * effacer ce qu'une personne a commencé à saisir ailleurs dans la liste.
 * Les premières cartes seules sont montrées ; les autres se dévoilent par lots.
 */

import { h } from '../../dom.js';
import { piecesParEtat, type EtatPiece, type PieceListee } from './api.js';
import { creerSection, type Section } from './communs.js';

export interface ListeCartes {
  readonly el: HTMLElement;
  /** Aligne la liste sur ces pièces : retire les absentes, ajoute les nouvelles, garde les autres. */
  maj(pieces: readonly PieceListee[]): void;
  retirer(id: number): void;
  remplacer(id: number, carte: HTMLElement): void;
  compte(): number;
}

const PAS = 20;

export function listeCartes(
  construire: (p: PieceListee) => HTMLElement,
  messageVide: string,
  surCompte: (n: number) => void,
): ListeCartes {
  const cartes = new Map<number, HTMLElement>();
  const racine = h('div', { classe: 'liste-cartes' });
  const vide = h('p', { classe: 'note-lecture' }, messageVide);
  const suite = h('button', { type: 'button', classe: 'btn-action' });
  const pied = h('div', { classe: 'barre-actions' }, suite);
  let limite = PAS;

  const appliquer = (): void => {
    let i = 0;
    for (const c of Array.from(racine.children)) (c as HTMLElement).hidden = i++ >= limite;
    const n = racine.children.length;
    pied.hidden = n <= limite;
    suite.textContent = `Afficher les ${String(Math.min(PAS, n - limite))} suivantes (${String(n - limite)} de plus)`;
    vide.hidden = n > 0;
    surCompte(n);
  };
  suite.addEventListener('click', () => {
    limite += PAS;
    appliquer();
  });

  return {
    el: h('div', {}, vide, racine, pied),
    maj(pieces) {
      const presentes = new Set(pieces.map((p) => p.id));
      for (const [id, c] of cartes) {
        if (!presentes.has(id)) {
          c.remove();
          cartes.delete(id);
        }
      }
      let place: Element | null = racine.firstElementChild;
      for (const p of pieces) {
        let c = cartes.get(p.id);
        if (c === undefined) {
          c = construire(p);
          cartes.set(p.id, c);
        }
        if (c === place) place = place.nextElementSibling;
        else racine.insertBefore(c, place);
      }
      appliquer();
    },
    retirer(id) {
      cartes.get(id)?.remove();
      cartes.delete(id);
      appliquer();
    },
    remplacer(id, carte) {
      cartes.get(id)?.replaceWith(carte);
      cartes.set(id, carte);
      appliquer();
    },
    compte: () => racine.children.length,
  };
}

export interface SectionCartes {
  readonly section: Section;
  readonly liste: ListeCartes;
  charger(): Promise<void>;
}

/** Une section dont le corps est la liste des pièces d'un état. */
export function sectionDeCartes(
  numero: number,
  titre: string,
  sousTitre: string,
  etat: EtatPiece,
  construire: (p: PieceListee, liste: ListeCartes) => HTMLElement,
  messageVide: string,
  ...avant: (Node | null)[]
): SectionCartes {
  const section = creerSection(numero, titre, sousTitre, ...avant);
  const liste: ListeCartes = listeCartes((p) => construire(p, liste), messageVide, (n) => {
    section.sousTitre(n);
  });
  section.corps.appendChild(liste.el);
  return {
    section,
    liste,
    async charger() {
      liste.maj((await piecesParEtat(etat)).pieces);
    },
  };
}
